import { Component, EventEmitter, HostBinding, Inject, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationFacade, PurchaseService } from '@ridango/components';
import { ActivationMode, NextButtonElement } from '@ridango/components/lib/purchase/purchase.model';
import { BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';
import { Temporal } from '@js-temporal/polyfill';
import { ProductComponent } from './product/product.component';
import { PurchasePageV2FacadeService } from './services/purchase-page-v2.facade.service';
import { CardsPurchaseFacadeService } from '@ridango/widgets';
import { PurchaseStep } from './models/purchase-step.model';

@Component({
    selector: 'gl-purchase-v2-base',
    templateUrl: './purchase-page-v2-base.component.html'
})
export class PurchasePageV2BaseComponent extends BaseComponent {
    ActivationMode = ActivationMode;

    @HostBinding('style.backgroundColor')
    get backgroundColor() {
        const isHighContrast = document.body.classList.contains('high-contrast');

        if (this.stepFromUrl$.value === 'ticket-activation') {
            if (isHighContrast) {
                return 'contrast-black';
            }
            return '#F0F0F0';
        }
        return 'transparent';
    }

    @ViewChild(ProductComponent) productComponent: ProductComponent;
    @Output() nextButtonElement = new EventEmitter<NextButtonElement>();

    private get steps(): PurchaseStep[] {
        return this.purchasePageFacade.steps;
    }

    readonly minDate = Temporal.Now.plainDateISO().toString();

    categoryProducts = [];

    isPurchaseByCard = this.cardsPurchaseFacadeService.isPurchaseByCard;

    activationMode$ = this.purchaseService.activationMode$;
    selectedStartingDate$ = this.purchaseService.selectedStartingDate$;

    savedPreference$ = this.purchasePageFacade.savedPreference$;
    selectedArea$ = this.purchasePageFacade.selectedArea$;
    productAreas$ = this.purchasePageFacade.productAreas$;
    areas$ = this.purchasePageFacade.areas$;
    preferences$ = this.purchasePageFacade.preferences$;
    dataFromUrl$ = this.purchasePageFacade.dataFromUrl$;
    selectedPreferenceOption$ = this.purchasePageFacade.selectedPreference$;
    stepFromUrl$ = this.purchasePageFacade.stepFromUrl$;

    constructor(
        @Inject('ENV') private readonly env: Env,
        private router: Router,
        private translate: TranslateService,
        private activatedRoute: ActivatedRoute,
        private purchaseService: PurchaseService,
        private notification: NotificationFacade,
        private purchasePageFacade: PurchasePageV2FacadeService,
        private cardsPurchaseFacadeService: CardsPurchaseFacadeService
    ) {
        super();
    }

    handleNextClicked(_step?: string): void {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        this.purchasePageFacade.handleNextClicked(urlSegments, _step);
    }

    handleNextButtonElement($event: NextButtonElement) {
        this.nextButtonElement.emit($event);
    }

    handleSelectedPreferenceChange(option): void {
        this.purchasePageFacade.handleSelectedOptionChange(option);
    }

    onActivationModeChange(mode: ActivationMode): void {
        this.purchaseService.setActivationMode(mode);
    }

    onDateChange(selectedDate: Temporal.PlainDate): void {
        this.purchaseService.setStartingDate(selectedDate);
    }

    handleErrorEvent(message?: string) {
        this.purchaseService.setPurchaseDataLoading(false);
        const normalizedMessage = typeof message === 'string' && message.trim().length > 0 ? message : null;
        this.router.navigate([this.translate.currentLang, 'purchase-v2', this.steps[0].name]);
        if (normalizedMessage) {
            this.notification.error(this.translate.instant(normalizedMessage));
        }
    }
}
